import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { ClassSelect } from "@/components/StudentPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const autoGrade = (pct) => (pct >= 90 ? "A+" : pct >= 80 ? "A" : pct >= 70 ? "B+" : pct >= 60 ? "B" : pct >= 50 ? "C+" : pct >= 40 ? "C" : pct >= 33 ? "D" : "E");
const splitMarks = (m) => { const x = /^\s*([\d.]+)\s*\/\s*([\d.]+)\s*$/.exec(m || ""); return x ? [x[1], x[2]] : [m || "", ""]; };

export function SubjectMarksForm({ yearNow, onDone }) {
  const [cls, setCls] = useState({ branch_id: "", class_name: "", section: "" });
  const [meta, setMeta] = useState({ academic_year: yearNow, term: "Term 1", subject: "", max: "100" });
  const [students, setStudents] = useState([]);
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!cls.class_name || !cls.section || !meta.subject.trim() || !/^\d{4}-\d{2}$/.test(meta.academic_year) || !meta.term) {
      if (cls.class_name && cls.section) api.get("/class-students", { params: cls }).then((r) => setStudents(r.data)).catch(() => setStudents([]));
      return;
    }
    const t = setTimeout(() => {
      Promise.all([api.get("/class-students", { params: cls }),
        api.get("/progress-reports", { params: { class_name: cls.class_name, section: cls.section, term: meta.term, academic_year: meta.academic_year, limit: 100 } })])
        .then(([s, p]) => {
          setStudents(s.data);
          const by = Object.fromEntries(p.data.items.map((r) => [r.student_id, r.subjects.find((x) => x.subject.trim().toLowerCase() === meta.subject.trim().toLowerCase())]));
          setRows(Object.fromEntries(s.data.map((st) => { const e = by[st.id]; const [m] = splitMarks(e?.marks); return [st.id, { marks: e ? m : "", grade: e?.grade || "", remarks: e?.remarks || "" }]; })));
        }).catch((e) => toast.error(formatApiError(e)));
    }, 400);
    return () => clearTimeout(t);
  }, [cls.branch_id, cls.class_name, cls.section, meta.subject, meta.term, meta.academic_year]);

  const max = Number(meta.max) || 0;
  function setMarks(id, v) {
    const n = Number(v);
    const grade = v !== "" && max > 0 && !Number.isNaN(n) ? autoGrade((n / max) * 100) : rows[id]?.grade || "";
    setRows({ ...rows, [id]: { ...rows[id], marks: v, grade } });
  }
  const bad = (v) => v !== "" && (Number.isNaN(Number(v)) || Number(v) < 0 || (max > 0 && Number(v) > max));

  async function save() {
    if (students.some((s) => bad(rows[s.id]?.marks || ""))) { toast.error(`Marks must be between 0 and ${max}`); return; }
    setBusy(true);
    try {
      const list = students.map((s) => { const r = rows[s.id] || {}; return { student_id: s.id, marks: r.marks ? (max ? `${r.marks}/${max}` : r.marks) : "", grade: r.grade || "", remarks: r.remarks || "" }; });
      const { data } = await api.post("/progress-reports/subject-bulk", { academic_year: meta.academic_year, term: meta.term, subject: meta.subject.trim(), rows: list });
      toast.success(`${meta.subject}: ${data.created} new reports, ${data.updated} updated${data.skipped ? `, ${data.skipped} left blank` : ""}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  }

  const ready = cls.class_name && meta.subject.trim() && meta.term && /^\d{4}-\d{2}$/.test(meta.academic_year);
  return (
    <div data-testid="marks-form" className="bg-white rounded-2xl shadow-sm p-5 mb-6 space-y-4">
      <h3 className="font-display font-bold text-slate-900">Enter one subject's marks for the whole class</h3>
      <div className="flex flex-wrap gap-3 items-end">
        <ClassSelect value={cls} onChange={setCls} />
        <div><Label>Academic year</Label><Input data-testid="marks-year" value={meta.academic_year} onChange={(e) => setMeta({ ...meta, academic_year: e.target.value })} className="mt-1 w-28" /></div>
        <div><Label>Term</Label><Input data-testid="marks-term" list="pr-terms" value={meta.term} onChange={(e) => setMeta({ ...meta, term: e.target.value })} className="mt-1 w-36" /></div>
        <div><Label>Subject</Label><Input data-testid="marks-subject" placeholder="e.g. Maths" value={meta.subject} onChange={(e) => setMeta({ ...meta, subject: e.target.value })} className="mt-1 w-40" /></div>
        <div><Label>Max marks</Label><Input data-testid="marks-max" type="number" min={1} value={meta.max} onChange={(e) => setMeta({ ...meta, max: e.target.value })} className="mt-1 w-24" /></div>
      </div>
      {!ready ? <p className="text-sm text-slate-500">Choose the class and type the subject to load students.</p> : students.length === 0 ? <p className="text-sm text-slate-500">No students in this class.</p> : (
        <div className="overflow-x-auto border border-violet-100 rounded-xl">
          <table className="text-sm min-w-full">
            <thead className="bg-violet-50"><tr><th className="text-left px-3 py-2">Student</th><th className="text-left px-2">Marks{max ? ` / ${max}` : ""}</th><th className="text-left px-2">Grade</th><th className="text-left px-2">Remarks</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {students.map((s, i) => (
                <tr key={s.id} data-testid={`marks-row-${s.id}`}>
                  <td className="px-3 py-1.5 font-semibold whitespace-nowrap">{s.name}</td>
                  <td className="px-1.5 py-1.5"><Input data-testid={`marks-value-${s.id}`} inputMode="decimal" value={rows[s.id]?.marks || ""} onChange={(e) => setMarks(s.id, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") document.querySelector(`[data-testid="marks-value-${students[i + 1]?.id}"]`)?.focus(); }}
                    className={`h-9 w-24 ${bad(rows[s.id]?.marks || "") ? "border-rose-500" : ""}`} /></td>
                  <td className="px-1.5 py-1.5"><Input data-testid={`marks-grade-${s.id}`} value={rows[s.id]?.grade || ""} onChange={(e) => setRows({ ...rows, [s.id]: { ...rows[s.id], grade: e.target.value.toUpperCase() } })} className="h-9 w-20" /></td>
                  <td className="px-1.5 py-1.5"><Input data-testid={`marks-remarks-${s.id}`} value={rows[s.id]?.remarks || ""} onChange={(e) => setRows({ ...rows, [s.id]: { ...rows[s.id], remarks: e.target.value } })} className="h-9 min-w-[220px]" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-slate-400">Grade fills in automatically from marks (A+ ≥ 90%, A ≥ 80%, B+ ≥ 70%, B ≥ 60%, C+ ≥ 50%, C ≥ 40%, D ≥ 33%, else E) — you can change it. Press Enter to jump to the next student.</p>
      <Button data-testid="marks-save" disabled={busy || !ready || !students.length} onClick={save} className="btn-brand">{busy ? "Saving…" : "Save marks"}</Button>
    </div>
  );
}
